/** New-tag-schema dialog: name + codename (+ description), POST, then navigate to
 *  the created schema's detail page to author the schema body. */
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tagSchemasApi from '@/api/tagSchemas';
import { useSnackbar } from '@/components/Snackbar';

const slugify = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export function TagSchemaCreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [codename, setCodename] = useState('');
  const [codenameTouched, setCodenameTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setCodename('');
    setCodenameTouched(false);
    setDescription('');
    setError(null);
  };

  const mutation = useMutation({
    mutationFn: (body: tagSchemasApi.TagSchemaWrite) => tagSchemasApi.create(body),
    onSuccess: (schema) => {
      snackbar.show('Schema created.', 'success');
      qc.invalidateQueries({ queryKey: ['tag_schemas'] });
      reset();
      onClose();
      navigate(`/tag-schemas/${schema.id}/schema`);
    },
    onError: (e) => setError(e instanceof ConsoleApiError ? e.message : 'Create failed.'),
  });

  const effectiveCodename = codenameTouched ? codename : slugify(name);
  const canSubmit = name.trim().length > 0 && effectiveCodename.length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New tag schema</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name" size="small" fullWidth value={name} required autoFocus
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Codename" size="small" fullWidth required
            helperText="Unique identifier (lowercase slug). Defaults from the name."
            value={effectiveCodename}
            onChange={(e) => { setCodenameTouched(true); setCodename(e.target.value); }}
            inputProps={{ maxLength: 64 }}
          />
          <TextField
            label="Description" size="small" fullWidth multiline minRows={2} value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {error && <Box sx={{ color: 'error.main', fontSize: 13 }}>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained" disabled={!canSubmit}
          onClick={() =>
            mutation.mutate({
              name: name.trim(),
              codename: effectiveCodename,
              description: description.trim(),
              schema: { type: 'object', properties: {}, required: [] },
            })
          }
        >
          {mutation.isPending ? 'Creating…' : 'Create schema'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
